import { Switch, SwitchRootProps } from '@chakra-ui/react';
import {
  ChangeEventHandler,
  FocusEventHandler,
  ReactNode,
  forwardRef,
} from 'react';

export interface MSwitchProps extends Omit<
  SwitchRootProps,
  'checked' | 'disabled' | 'readOnly' | 'onChange' | 'onBlur' | 'onFocus'
> {
  isChecked?: boolean;
  isDisabled?: boolean;
  isReadOnly?: boolean;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  onFocus?: FocusEventHandler<HTMLInputElement>;
  id?: string;
  name?: string;
  required?: boolean;
  autoFocus?: boolean;
  tabIndex?: number;
  'data-testid'?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
  /** Rare override — see the header note on `role`/`aria-checked`. */
  role?: string;
  'aria-checked'?: boolean;
  children?: ReactNode;
}

/*
 * v3 Switch is namespace-only (`Switch.Root/Control/HiddenInput/Label`), no
 * bare renderable component. This wrapper keeps the v2 call-site shape
 * (`isChecked`/`isDisabled`/`onChange`) — see
 * `styles/chakra/components/Switch.ts` for the recipe this composes
 * against.
 *
 * Same prop split as `MCheckbox`: accessible-element props (id, name, the
 * aria-* attrs, the event handlers, the forwarded ref) land on
 * `Switch.HiddenInput`, the real native `<input type="checkbox">` — a
 * switch has no native ARIA role of its own in v3 (it's a real checkbox
 * input under the hood), so a caller needing `role="switch"` for a specific
 * a11y requirement passes it here and it's applied to that same real input,
 * not the `Root` label wrapper. Everything else (style props, `checked`/
 * `disabled`/`readOnly` state, `variant`/`size`) goes on `Root`.
 *
 * `data-testid` goes on `Root` too, as it did in v2 (the root `<label>`). On
 * the 1×1 clipped input, Playwright never finds a stable box to click, and
 * QA's e2e suite clicks these testids; its checked/disabled assertions follow
 * a label to its control, so they still read the input.
 *
 * `id` is the exception: it goes to `Root` as `ids.hiddenInput`, not straight
 * onto `HiddenInput`. `Root` is a `<label htmlFor>` pointing at zag's own
 * hidden-input id; overriding the input's `id` directly left that `htmlFor`
 * dangling, so clicking the switch did nothing.
 */
export const MSwitch = forwardRef<HTMLInputElement, MSwitchProps>(
  (
    {
      isChecked,
      isDisabled,
      isReadOnly,
      onChange,
      onBlur,
      onFocus,
      id,
      name,
      required,
      autoFocus,
      tabIndex,
      'data-testid': dataTestId,
      'aria-label': ariaLabel,
      'aria-describedby': ariaDescribedBy,
      role,
      'aria-checked': ariaChecked,
      children,
      ...rootProps
    },
    ref,
  ) => (
    <Switch.Root
      checked={isChecked}
      disabled={isDisabled}
      readOnly={isReadOnly}
      {...(id && { ids: { hiddenInput: id } })}
      data-testid={dataTestId}
      {...rootProps}
    >
      <Switch.HiddenInput
        ref={ref}
        name={name}
        required={required}
        autoFocus={autoFocus}
        tabIndex={tabIndex}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        role={role}
        aria-checked={ariaChecked}
        // zag's read-only guard only cancels the click, and React fires a
        // checkbox's onChange from that same click — so the toggle reached
        // forms on locked screens. v2 skipped onChange when read-only.
        onChange={isReadOnly ? undefined : onChange}
        onBlur={onBlur}
        onFocus={onFocus}
      />
      <Switch.Control />
      {children && <Switch.Label>{children}</Switch.Label>}
    </Switch.Root>
  ),
);

MSwitch.displayName = 'MSwitch';

export default MSwitch;
